import React, { useState } from 'react';
import { Translate } from 'react-localize-redux';
import { TextField, Typography } from '@mui/material';
import Modal from '../../../components/general/modal';

/**
 * Shared "reason-prompt" for row actions that must capture a free-text reason
 * before a confirmation (SHER-537 delete, SHER-570 annul). Both were previously
 * duplicated verbatim in SubmissionsTable; this collapses them into one hook +
 * one modal configured per action.
 */

export interface ReasonPromptState {
    pendingIndex: number | null;
    reason: string;
    setReason: (value: string) => void;
    error: string | null;
    loading: boolean;
    open: (index: number) => void;
    close: () => void;
    submit: (opts: {
        requiredKey: string;
        action: (reason: string) => Promise<any>;
        resolveError: (error: any) => string;
    }) => Promise<void>;
}

export function useReasonPrompt(): ReasonPromptState {
    const [pendingIndex, setPendingIndex] = useState<number | null>(null);
    const [reason, setReason] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    const open = (index: number) => {
        setPendingIndex(index);
        setReason('');
        setError(null);
    };

    const close = () => {
        if (loading) {
            return;
        }
        setPendingIndex(null);
        setReason('');
        setError(null);
    };

    const submit: ReasonPromptState['submit'] = async ({ requiredKey, action, resolveError }) => {
        const trimmed = reason.trim();
        if (!trimmed) {
            setError(requiredKey);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            await action(trimmed);
            setPendingIndex(null);
            setReason('');
            setError(null);
        } catch (error: any) {
            setError(resolveError(error));
        } finally {
            setLoading(false);
        }
    };

    return { pendingIndex, reason, setReason, error, loading, open, close, submit };
}

interface ReasonModalProps {
    prompt: ReasonPromptState;
    // Modal's title prop is typed narrowly (string | object), so keep it an element/string.
    title: React.ReactElement | string;
    body: React.ReactNode;
    reasonLabel: React.ReactNode;
    confirmLabel: string;
    onConfirm: () => void;
}

export const ReasonModal: React.FC<ReasonModalProps> = ({ prompt, title, body, reasonLabel, confirmLabel, onConfirm }) => {
    if (prompt.pendingIndex === null) {
        return null;
    }
    return (
        <Modal
            open
            title={title}
            closeModal={prompt.close}
            confirmAction={onConfirm}
            confirmButtonLabel={confirmLabel}
            disabled={prompt.loading}
        >
            <div style={{ minWidth: 320 }}>
                <Typography variant="body2" style={{ marginBottom: 16 }}>
                    {body}
                </Typography>
                <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    label={reasonLabel}
                    value={prompt.reason}
                    onChange={(event) => prompt.setReason(event.target.value)}
                    disabled={prompt.loading}
                />
                {prompt.error && (
                    <Typography color="error" variant="body2" style={{ marginTop: 12 }}>
                        {prompt.error.startsWith('hospital.') ? <Translate id={prompt.error} /> : prompt.error}
                    </Typography>
                )}
            </div>
        </Modal>
    );
};
